import * as React from 'react'

import { ChevronDownIcon, GripVerticalIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { ISiteLocation, PermitFieldType } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { WORKFLOW_PIN_CARD_SURFACE_CLASS } from '~/client/src/shared/components/MapAnchoredCard/workflowPinCardAnchor'
import { PERMIT_FIELD_GLYPHS } from '~/client/src/shared/components/PermitFieldIcon/permitFieldGlyphs'
import {
  Field,
  FieldContent,
  FieldError,
  FieldTitle,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import HoursOfOperationNote, {
  getDateNotes,
  HoursOfOperationNoteList,
} from '~/client/src/shared/components/SitePermitCreationForm/components/HoursOfOperationNote'
import SitePermitCreationFormStore from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm.store'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import { locationPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ComposerFooter from './ComposerFooter'
import WorkflowComposerStore, { IComposerRow } from './WorkflowComposer.store'

interface IProps {
  store: WorkflowComposerStore
  isMobileEnv?: boolean
}

const RequiredMark: React.FC = () => (
  <span className="tw:text-destructive" aria-hidden="true">
    *
  </span>
)

// The same notes the form shows under the field.
const ComposerRowNote: React.FC<{
  row: IComposerRow
  formStore: SitePermitCreationFormStore
}> = observer(({ row, formStore }) => {
  if (row.type === PermitFieldType.StartFinishDates) {
    return (
      <HoursOfOperationNoteList notes={getDateNotes(formStore)} isSummarized />
    )
  }
  if (!locationPermitFieldTypes.includes(row.type)) return null

  // A multi-select field stores its locations as one array value.
  const values =
    formStore.workflowFieldsStore.getFieldValues<
      ISiteLocation | ISiteLocation[]
    >(row.id) ?? []
  return (
    <HoursOfOperationNote
      store={formStore}
      locationIds={values.flat().map(location => location?.id)}
    />
  )
})

// FieldTitle (not FieldLabel): the value is a picker Button, not a labelable input.
// 'columns' keeps the caption a fixed column so every picker starts at the same x;
// the caption's min height keeps it level with the picker above a note.
const ComposerRow: React.FC<{
  row: IComposerRow
  formStore: SitePermitCreationFormStore
}> = ({ row, formStore }) => {
  const Glyph = PERMIT_FIELD_GLYPHS[row.type]

  return (
    <Field orientation="columns">
      <FieldTitle className="tw:min-h-8 tw:w-[150px] tw:shrink-0 tw:gap-1 tw:text-body-sm tw:text-muted-foreground">
        <Glyph className="tw:size-4 tw:shrink-0" aria-hidden="true" />
        {row.caption}
        {row.isRequired && <RequiredMark />}
      </FieldTitle>
      <FieldContent className="tw:min-w-0 tw:gap-1">
        <Button
          variant="outline"
          onClick={e => row.open(e.currentTarget)}
          className="tw:min-w-0 tw:justify-between tw:font-normal tw:text-foreground"
        >
          <span
            className={
              row.value
                ? 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-left'
                : 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-left tw:text-muted-foreground'
            }
          >
            {row.value || Localization.translator.select}
          </span>
          <ChevronDownIcon
            data-icon="inline-end"
            className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
        <ComposerRowNote row={row} formStore={formStore} />
      </FieldContent>
    </Field>
  )
}

const WorkflowComposerCard: React.FC<IProps> = observer(
  ({ store, isMobileEnv }) => (
    <div
      data-testid="workflow-composer-card"
      className={
        isMobileEnv
          ? // Extra bottom padding clears the fixed action bar that sits below.
            'tw:flex tw:w-full tw:flex-col tw:gap-2.5 tw:bg-popover tw:p-4 tw:pb-24'
          : `tw:flex tw:w-[400px] tw:flex-col tw:gap-2.5 tw:p-4 ${WORKFLOW_PIN_CARD_SURFACE_CLASS}`
      }
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2">
          {/* No grip on mobile — it would fight the vaul sheet's own drag gesture. */}
          {!isMobileEnv && (
            <button
              type="button"
              draggable={false}
              onDragStart={e => e.preventDefault()}
              onPointerDown={store.anchorController.startCardDrag}
              aria-label={Localization.translator.move}
              className="tw:flex tw:shrink-0 tw:cursor-grab tw:touch-none tw:appearance-none tw:items-center tw:border-0 tw:bg-transparent tw:p-0 tw:text-muted-foreground tw:outline-none"
            >
              <GripVerticalIcon
                className="tw:pointer-events-none tw:size-4"
                aria-hidden="true"
              />
            </button>
          )}
          {/* One offerable type: a disabled trigger would advertise a missing action. */}
          {store.canChangeWorkflowType ? (
            <FieldTrigger
              onClick={e => store.openTypePicker(e.currentTarget)}
              aria-label={Localization.translator.workflowType}
              className="tw:min-w-0 tw:flex-1"
              data-testid="workflow-composer-type"
            >
              <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
                <WorkflowTypeIcon
                  iconName={store.workflowType?.iconName}
                  size="small"
                />
                <span className="tw:truncate">{store.workflowType?.name}</span>
              </span>
            </FieldTrigger>
          ) : (
            <Tag
              // Overrides Tag's shrink-0 so a long name ellipsises.
              className="tw:min-w-0 tw:shrink!"
              icon={
                <WorkflowTypeIcon
                  iconName={store.workflowType?.iconName}
                  size="small"
                />
              }
            >
              {store.workflowType?.name}
            </Tag>
          )}
        </div>
        {/* Desktop only — the mobile card doesn't collapse; the bottom-drawer handles that. */}
        {!isMobileEnv && (
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
            <Button
              variant="outline"
              onClick={store.minimize}
              aria-label={Localization.translator.minimize}
              data-testid="workflow-composer-minimize"
            >
              {Localization.translator.minimize}
              <Kbd data-icon="inline-end">Esc</Kbd>
            </Button>
          </div>
        )}
      </div>
      <div className="tw:flex tw:items-center tw:gap-1">
        <Input
          value={store.titleValue}
          onChange={e => store.setTitle(e.target.value)}
          placeholder={Localization.translator.enterWorkflowTitle}
          aria-label={Localization.translator.enterWorkflowTitle}
          aria-required={store.isTitleRequired}
          data-testid="workflow-composer-title"
          // Typing/selection in a vaul sheet must not drag it (mobile bottom-sheet).
          data-vaul-no-drag=""
          // Mobile auto-focus pops the keyboard over the collapsed sheet.
          autoFocus={!isMobileEnv}
          className="tw:h-8 tw:border-0 tw:bg-transparent tw:px-0 tw:shadow-none tw:focus-visible:ring-0"
        />
        {store.isTitleRequired && <RequiredMark />}
      </div>
      <Separator />
      {/* Admin-configured rows. Mobile: Activity's four plus a half row hinting it scrolls. */}
      <div
        className={
          isMobileEnv
            ? 'tw:flex tw:max-h-[174px] tw:flex-col tw:gap-2.5 tw:overflow-y-auto'
            : 'tw:flex tw:max-h-[40vh] tw:flex-col tw:gap-2.5 tw:overflow-y-auto'
        }
      >
        {store.composerRows.map(row => (
          <ComposerRow key={row.id} row={row} formStore={store.formStore} />
        ))}
      </div>
      {/* role={undefined} drops FieldError's role="alert": this is standing state, and a
          live region would announce on open and re-announce on every keystroke. */}
      {!store.canSubmit &&
        !store.isSubmitting &&
        store.invalidFieldCaptions.length > 0 && (
          <FieldError role={undefined}>
            {`${
              Localization.translator.required
            }: ${store.invalidFieldCaptions.join(', ')}`}
          </FieldError>
        )}
      {/* Mobile renders these in the fixed action bar so they stay visible while collapsed. */}
      {!isMobileEnv && (
        <div className="tw:border-t tw:border-border tw:pt-4">
          <ComposerFooter
            canSubmit={store.canSubmit}
            isSubmitting={store.isSubmitting}
            onCancel={store.close}
            onSubmit={() => void store.submit()}
          />
        </div>
      )}
    </div>
  ),
)

export default WorkflowComposerCard
